/**
 * Utility functions for detecting and linking Ombudsman decision references in text
 */

/**
 * Regular expression to match DRN references (e.g., DRN-1234567, DRN-5109125)
 * Matches: DRN- followed by 1-10 digits
 */
const DRN_PATTERN = /\bDRN-(\d{1,10})\b/gi;

/**
 * Regular expression to match UUID format (for decision IDs)
 * Matches standard UUID v4 format
 */
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;

/**
 * Check if a string looks like a DRN reference
 */
export function isDRNReference(str: string): boolean {
  return /^DRN-\d{1,10}$/i.test(str.trim());
}

/**
 * Check if a string looks like a UUID
 */
export function isUUID(str: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());
}

/**
 * Extract all DRN references from a text string
 * Returns an array of DRN references found (without duplicates)
 */
export function extractDRNReferences(text: string): string[] {
  if (!text) return [];
  
  const matches = text.match(DRN_PATTERN);
  if (!matches) return [];
  
  // Return unique references, case-insensitive but preserve original case
  const uniqueRefs = new Set<string>();
  matches.forEach(match => {
    uniqueRefs.add(match.toUpperCase());
  });
  
  return Array.from(uniqueRefs);
}

/**
 * Extract all UUID references from a text string
 * Returns an array of UUIDs found (without duplicates)
 */
export function extractUUIDReferences(text: string): string[] {
  if (!text) return [];
  
  const matches = text.match(UUID_PATTERN);
  if (!matches) return [];
  
  // Return unique UUIDs
  return Array.from(new Set(matches));
}

/**
 * Check if text contains any decision references (DRN or UUID)
 */
export function containsDecisionReferences(text: string): boolean {
  if (!text) return false;
  return DRN_PATTERN.test(text) || UUID_PATTERN.test(text);
}

/**
 * Interface for a decision reference segment in text
 */
export interface DecisionReferenceSegment {
  text: string;
  isReference: boolean;
  reference: string | null;
  type: 'drn' | 'uuid' | null;
}

/**
 * Split text into segments, identifying decision references
 * Useful for rendering text with clickable decision links
 */
export function parseDecisionReferences(text: string): DecisionReferenceSegment[] {
  if (!text) return [];
  
  const segments: DecisionReferenceSegment[] = [];
  let lastIndex = 0;
  
  // Find all DRN references
  const drnMatches: Array<{ index: number; text: string }> = [];
  let match;
  DRN_PATTERN.lastIndex = 0; // Reset regex
  while ((match = DRN_PATTERN.exec(text)) !== null) {
    drnMatches.push({
      index: match.index,
      text: match[0].toUpperCase(),
    });
  }
  
  // Find all UUID references
  const uuidMatches: Array<{ index: number; text: string }> = [];
  UUID_PATTERN.lastIndex = 0; // Reset regex
  while ((match = UUID_PATTERN.exec(text)) !== null) {
    uuidMatches.push({
      index: match.index,
      text: match[0],
    });
  }
  
  // Combine and sort all matches by position
  const allMatches = [
    ...drnMatches.map(m => ({ ...m, type: 'drn' as const })),
    ...uuidMatches.map(m => ({ ...m, type: 'uuid' as const })),
  ].sort((a, b) => a.index - b.index);
  
  // Build segments
  for (const match of allMatches) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, match.index),
        isReference: false,
        reference: null,
        type: null,
      });
    }
    
    // Add the reference match
    segments.push({
      text: match.text,
      isReference: true,
      reference: match.text,
      type: match.type,
    });
    
    lastIndex = match.index + match.text.length;
  }
  
  // Add remaining text after last match
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      isReference: false,
      reference: null,
      type: null,
    });
  }
  
  // If no matches found, return the whole text as a single segment
  if (segments.length === 0) {
    segments.push({
      text,
      isReference: false,
      reference: null,
      type: null,
    });
  }
  
  return segments;
}

